import {
  CHANNELS,
  type Channel,
  type EscalationTarget,
  type NotificationRule,
  type RepeatEvery,
  type RuleCode,
  type RuleUpdate,
} from './types';

// Formulario de edición de una regla (so=370). Puro: estado del formulario, errores con
// los mismos límites que el backend (so=330 settings.ts) y cuerpo exacto del PUT, que
// admite solo los campos de cada regla.

export interface RuleForm {
  enabled: boolean;
  channels: Channel[];
  thresholdPct: string;
  leadDays: string;
  escalateAfterDays: string;
  repeatEvery: RepeatEvery;
  escalationTarget: EscalationTarget;
}

export type RuleField = 'channels' | 'thresholdPct' | 'leadDays' | 'escalateAfterDays';

/** Qué campos numéricos y selectores lleva cada regla, además de activa y canales. */
export const RULE_FIELDS: Record<
  RuleCode,
  { threshold?: true; lead?: true; repeat?: true; escalation?: true }
> = {
  'stage.pending': {},
  escalation: {},
  'stage.half_elapsed': { threshold: true },
  'stage.overdue': { repeat: true, escalation: true },
  'review.upcoming': { lead: true, repeat: true },
  'custom_date.upcoming': {},
  'custom_date.overdue': {},
  // G3
  'request.submitted': {},
  'request.resolved': {},
  // G2-C
  'training.lms_unreported': {},
  'training.no_recipients': {},
};

const LIMITS = {
  thresholdPct: { min: 1, max: 99, error: 'Entre 1 y 99 %.' },
  leadDays: { min: 1, max: 730, error: 'Entre 1 y 730 días.' },
  escalateAfterDays: { min: 0, max: 90, error: 'Entre 0 y 90 días.' },
} as const;

/** Entero dentro de [min, max] escrito solo con dígitos; si no, null. */
export function parseIntIn(raw: string, min: number, max: number): number | null {
  if (!/^\d{1,4}$/.test(raw.trim())) {
    return null;
  }
  const n = Number(raw.trim());
  return n >= min && n <= max ? n : null;
}

export function formFromRule(rule: NotificationRule): RuleForm {
  return {
    enabled: rule.enabled,
    channels: [...rule.channels],
    thresholdPct: String(rule.thresholdPct ?? 50),
    leadDays: String(rule.leadDays ?? 90),
    escalateAfterDays: String(rule.escalateAfterDays ?? 2),
    repeatEvery: rule.repeatEvery ?? 'daily',
    escalationTarget: rule.escalationTarget ?? 'department_supervisors',
  };
}

/** Agrega o quita un canal manteniendo el orden canónico (plataforma, correo). */
export function toggleChannel(channels: Channel[], c: Channel): Channel[] {
  const next = channels.includes(c) ? channels.filter((x) => x !== c) : [...channels, c];
  return CHANNELS.filter((x) => next.includes(x));
}

export function ruleFormErrors(code: RuleCode, form: RuleForm): Partial<Record<RuleField, string>> {
  const f = RULE_FIELDS[code];
  const out: Partial<Record<RuleField, string>> = {};
  if (form.channels.length === 0) {
    out.channels = 'Elige al menos un canal.';
  }
  const check = (field: keyof typeof LIMITS, on: boolean | undefined): void => {
    const l = LIMITS[field];
    if (on && parseIntIn(form[field], l.min, l.max) === null) {
      out[field] = l.error;
    }
  };
  check('thresholdPct', f.threshold);
  check('leadDays', f.lead);
  check('escalateAfterDays', f.escalation);
  return out;
}

/** Cuerpo del PUT; solo se llama con un formulario sin errores. */
export function ruleBody(code: RuleCode, form: RuleForm): RuleUpdate {
  const f = RULE_FIELDS[code];
  const num = (field: keyof typeof LIMITS): number =>
    parseIntIn(form[field], LIMITS[field].min, LIMITS[field].max) ?? LIMITS[field].min;
  return {
    enabled: form.enabled,
    channels: form.channels,
    ...(f.threshold ? { thresholdPct: num('thresholdPct') } : {}),
    ...(f.lead ? { leadDays: num('leadDays') } : {}),
    ...(f.repeat ? { repeatEvery: form.repeatEvery } : {}),
    ...(f.escalation
      ? { escalateAfterDays: num('escalateAfterDays'), escalationTarget: form.escalationTarget }
      : {}),
  };
}
